/**
 * Authentication with the Firebase JS SDK (Identity Platform): e-mail + password,
 * password reset, and the set-password action page. This file and the login
 * pages are the only frontend pieces tied to the identity provider (plan §7.4).
 */
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  type Auth,
  confirmPasswordReset,
  connectAuthEmulator,
  getAuth,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
  verifyPasswordResetCode,
} from "firebase/auth";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

import { loadConfig, type RuntimeConfig } from "../lib/config";

interface AuthState {
  ready: boolean;
  user: User | null;
  config: RuntimeConfig | null;
  error: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  sendReset(email: string): Promise<void>;
  verifyResetCode(code: string): Promise<string>;
  confirmReset(code: string, password: string): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
let authInstance: Auth | null = null;

export async function idToken(force = false): Promise<string | null> {
  const user = authInstance?.currentUser;
  return user ? user.getIdToken(force) : null;
}

function requireAuth(): Auth {
  if (!authInstance) throw new Error("Authentication is not ready yet.");
  return authInstance;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    loadConfig()
      .then((runtime) => {
        setConfig(runtime);
        const app: FirebaseApp = initializeApp(runtime.firebase);
        const auth = getAuth(app);
        if (runtime.auth_emulator_url) {
          connectAuthEmulator(auth, runtime.auth_emulator_url, { disableWarnings: true });
        }
        authInstance = auth;
        unsubscribe = onIdTokenChanged(auth, (next) => {
          setUser(next);
          setReady(true);
        });
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : String(reason));
        setReady(true);
      });
    return () => unsubscribe?.();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      config,
      error,
      async signIn(email, password) {
        await signInWithEmailAndPassword(requireAuth(), email.trim(), password);
      },
      async signOut() {
        await firebaseSignOut(requireAuth());
      },
      async sendReset(email) {
        await sendPasswordResetEmail(requireAuth(), email.trim(), {
          url: `${window.location.origin}/login`,
        });
      },
      verifyResetCode: (code) => verifyPasswordResetCode(requireAuth(), code),
      confirmReset: (code, password) => confirmPasswordReset(requireAuth(), code, password),
    }),
    [ready, user, config, error],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth outside AuthProvider");
  return context;
}

export function friendlyAuthError(reason: unknown): string {
  const code = (reason as { code?: string })?.code ?? "";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found")) {
    return "The e-mail address or password is not correct.";
  }
  if (code.includes("too-many-requests")) return "Too many attempts. Wait a moment, then try again.";
  if (code.includes("user-disabled")) return "This account is deactivated. Contact your administrator.";
  if (code.includes("password-does-not-meet-requirements") || code.includes("weak-password")) {
    return "Choose a password of at least 12 characters.";
  }
  if (code.includes("expired-action-code") || code.includes("invalid-action-code")) {
    return "This link has expired or was already used. Use “Forgot password” to get a new one.";
  }
  return "Something went wrong. Try again.";
}
